import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createPlayer,
  createPlayerLink,
  deletePlayer,
  deletePlayerLink,
  approvePlayerPhotoRequest,
  getPlayerPhotoRequests,
  getCoachUsers,
  getRoster,
  rejectPlayerPhotoRequest,
  removeCoachPlayerPhoto,
  updatePlayer,
  uploadCoachPlayerPhoto,
  type ApiError,
} from "@/lib/api";
import { invalidatePlayerPhoto } from "@/features/player-photos/usePlayerPhoto";
import { invalidateScope, useScopedKey } from "@/lib/queryClient";
import { useScopeStore } from "@/stores/scope";
import type {
  CreatePlayerRequest,
  CreatePlayerUserLinkRequest,
  Player,
  UpdatePlayerRequest,
} from "@/lib/types";

/**
 * Roster data hooks for the M6a Coach > Roster list page.
 *
 * `useRoster` is the player list (the backend returns inactive
 * players too — the page filters client-side). `useCoachUsers` is
 * the linkable-team-user list for the link modal's account picker.
 * `useRosterMutations` bundles the five write paths (create /
 * update / delete player, link / unlink account).
 *
 * Scope: both queries are `useScopedKey`-keyed and gated on
 * `teamId` — the backend 409s a coach request with no resolved
 * team scope, so we don't fire until `useMe()` has reconciled it
 * (the M5 CoachReview latent-bug pattern). Every mutation
 * `invalidateScope()`s on success so the roster + KPIs refetch.
 */

export function useRoster() {
  const teamId = useScopeStore((s) => s.teamId);
  return useQuery({
    queryKey: useScopedKey("roster"),
    queryFn: getRoster,
    enabled: !!teamId,
  });
}

/** Linkable team users — only fetched when the link modal needs
 * them (the caller passes `enabled`). */
export function useCoachUsers(enabled: boolean) {
  const teamId = useScopeStore((s) => s.teamId);
  return useQuery({
    queryKey: useScopedKey("coach-users"),
    queryFn: getCoachUsers,
    enabled: !!teamId && enabled,
  });
}

export function usePlayerPhotoRequests(enabled: boolean) {
  const teamId = useScopeStore((s) => s.teamId);
  return useQuery({
    queryKey: useScopedKey("player-photo-requests", { status: "pending" }),
    queryFn: () => getPlayerPhotoRequests("pending"),
    enabled: !!teamId && enabled,
  });
}

export type RosterMutations = {
  createPlayer: (body: CreatePlayerRequest) => Promise<{ player: Player }>;
  updatePlayer: (
    id: string,
    body: UpdatePlayerRequest,
  ) => Promise<{ player: Player }>;
  deletePlayer: (id: string) => Promise<{ ok: true }>;
  linkAccount: (
    body: CreatePlayerUserLinkRequest,
  ) => Promise<{ ok: true; player: Player }>;
  unlinkAccount: (linkId: number) => Promise<{ ok: true }>;
  uploadPlayerPhoto: (id: string, file: File) => Promise<{ player: Player }>;
  removePlayerPhoto: (id: string) => Promise<{ player: Player }>;
  approvePhotoRequest: (id: string) => Promise<{ player: Player }>;
  rejectPhotoRequest: (id: string) => Promise<{ ok: true }>;
  /** True while any roster write is in flight. */
  isPending: boolean;
  /** The last roster-write error, if any. */
  error: ApiError | null;
};

/** The five roster write paths. Each `invalidateScope()`s on
 * success so the roster list + KPI tiles refetch. Returns plain
 * async functions (not the raw mutation objects) so callers just
 * `await` them; `isPending` / `error` aggregate across all five. */
export function useRosterMutations(): RosterMutations {
  const create = useMutation({
    mutationFn: createPlayer,
    onSuccess: () => invalidateScope(),
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdatePlayerRequest }) =>
      updatePlayer(id, body),
    onSuccess: () => invalidateScope(),
  });
  const remove = useMutation({
    mutationFn: deletePlayer,
    onSuccess: () => invalidateScope(),
  });
  const link = useMutation({
    mutationFn: createPlayerLink,
    onSuccess: () => invalidateScope(),
  });
  const unlink = useMutation({
    mutationFn: deletePlayerLink,
    onSuccess: () => invalidateScope(),
  });
  const uploadPhoto = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      uploadCoachPlayerPhoto(id, file),
    onSuccess: (_data, vars) => {
      invalidatePlayerPhoto("player", vars.id);
      void invalidateScope();
    },
  });
  const removePhoto = useMutation({
    mutationFn: removeCoachPlayerPhoto,
    onSuccess: (_data, id) => {
      invalidatePlayerPhoto("player", id);
      void invalidateScope();
    },
  });
  const approvePhoto = useMutation({
    mutationFn: approvePlayerPhotoRequest,
    onSuccess: (data) => {
      invalidatePlayerPhoto("player", data.player.id);
      void invalidateScope();
    },
  });
  const rejectPhoto = useMutation({
    mutationFn: rejectPlayerPhotoRequest,
    onSuccess: () => invalidateScope(),
  });

  const all = [
    create,
    update,
    remove,
    link,
    unlink,
    uploadPhoto,
    removePhoto,
    approvePhoto,
    rejectPhoto,
  ];
  return {
    createPlayer: (body) => create.mutateAsync(body),
    updatePlayer: (id, body) => update.mutateAsync({ id, body }),
    deletePlayer: (id) => remove.mutateAsync(id),
    linkAccount: (body) => link.mutateAsync(body),
    unlinkAccount: (linkId) => unlink.mutateAsync(linkId),
    uploadPlayerPhoto: async (id, file) => uploadPhoto.mutateAsync({ id, file }),
    removePlayerPhoto: (id) => removePhoto.mutateAsync(id),
    approvePhotoRequest: async (id) => approvePhoto.mutateAsync(id),
    rejectPhotoRequest: async (id) => {
      await rejectPhoto.mutateAsync(id);
      return { ok: true };
    },
    isPending: all.some((m) => m.isPending),
    error: (all.find((m) => m.error)?.error as ApiError | undefined) ?? null,
  };
}
